<template>
  <div class="dashboard-admin-dkp">
    <div class="dashboard-tabs-header">
      <button
        :class="['dashboard-tab-btn', { active: activeTab === 'admin' }]"
        @click="activeTab = 'admin'"
      >
        <feather-icon icon="ActivityIcon" size="16" />
        <span>Administrativo</span>
      </button>
      <button
        :class="['dashboard-tab-btn', { active: activeTab === 'gamificacao' }]"
        @click="activeTab = 'gamificacao'"
      >
        <feather-icon icon="AwardIcon" size="16" />
        <span>Gamificação</span>
      </button>
    </div>

    <gamification-dashboard v-if="activeTab === 'gamificacao'" />

    <b-overlay v-else :show="isLoading" variant="light" blur="2px">
      <div class="page-header">
        <p v-if="nomeUsuarioLogado" class="page-greeting">Olá, {{ nomeUsuarioLogado }}!</p>
        <h2 class="page-title">Dashboard Administrativo</h2>
        <p class="page-subtitle">Visão geral do sistema SalesFlow</p>
      </div>

      <div class="stats-grid">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><feather-icon icon="UsersIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Usuários Ativos</span>
            <span class="stat-value">{{ data.usuarios.usuariosAtivos || 0 }}</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><feather-icon icon="UserCheckIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Total Usuários</span>
            <span class="stat-value">{{ data.usuarios.totalUsuarios || 0 }}</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><feather-icon icon="BriefcaseIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Clientes Ativos</span>
            <span class="stat-value">{{ data.clientes.clientesAtivos || 0 }}</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><feather-icon icon="TruckIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Representantes</span>
            <span class="stat-value">{{ data.representantes.representantesAtivos || 0 }}</span>
          </div>
        </div>
      </div>

      <b-row class="mt-2">
        <b-col md="6">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Usuários por Perfil</h5>
            <vue-apex-charts
              v-if="perfilChartOptions.labels.length"
              type="donut"
              height="320"
              :options="perfilChartOptions"
              :series="perfilChartSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Pedidos por Mês (Últimos 12 meses)</h5>
            <vue-apex-charts
              v-if="pedidosMensaisOptions.xaxis.categories.length"
              type="bar"
              height="320"
              :options="pedidosMensaisOptions"
              :series="pedidosMensaisSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col md="12">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Faturamento Mensal (R$)</h5>
            <vue-apex-charts
              v-if="faturamentoOptions.xaxis.categories.length"
              type="area"
              height="300"
              :options="faturamentoOptions"
              :series="faturamentoSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col md="4">
          <b-card class="info-card">
            <h5 class="card-chart-title">Resumo de Clientes</h5>
            <div class="info-list">
              <div class="info-item">
                <span class="info-label">Total</span>
                <span class="info-value">{{ data.clientes.totalClientes || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Ativos</span>
                <span class="info-value text-success">{{ data.clientes.clientesAtivos || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Inativos</span>
                <span class="info-value text-danger">{{ data.clientes.clientesInativos || 0 }}</span>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="info-card">
            <h5 class="card-chart-title">Resumo de Usuários</h5>
            <div class="info-list">
              <div class="info-item">
                <span class="info-label">Total</span>
                <span class="info-value">{{ data.usuarios.totalUsuarios || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Ativos</span>
                <span class="info-value text-success">{{ data.usuarios.usuariosAtivos || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Inativos</span>
                <span class="info-value text-danger">{{ data.usuarios.usuariosInativos || 0 }}</span>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="info-card">
            <h5 class="card-chart-title">Representantes</h5>
            <div class="info-list">
              <div class="info-item">
                <span class="info-label">Total</span>
                <span class="info-value">{{ data.representantes.totalRepresentantes || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Ativos</span>
                <span class="info-value text-success">{{ data.representantes.representantesAtivos || 0 }}</span>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import axios from '@/libs/axios';
import { BOverlay, BRow, BCol, BCard } from 'bootstrap-vue';
import GamificationDashboard from '@/views/gamification/GamificationDashboard.vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'DashboardAdminDKP',
  components: { VueApexCharts, BOverlay, BRow, BCol, BCard, GamificationDashboard, ToastificationContent },
  data() {
    return {
      activeTab: 'admin',
      isLoading: false,
      data: {
        usuarios: {},
        perfis: [],
        pedidosRecentes: [],
        clientes: {},
        representantes: {},
      },
    };
  },
  computed: {
    // Saudação: primeiro nome do usuário logado (userData plano ou aninhado)
    nomeUsuarioLogado() {
      try {
        const ud = JSON.parse(localStorage.getItem('userData') || 'null');
        const nome = ud?.nome || ud?.userData?.nome || '';
        return String(nome).trim().split(/\s+/)[0] || '';
      } catch (e) {
        return '';
      }
    },
    chartTheme() {
      return this.$store?.state?.appConfig?.layout?.skin === 'dark' ? 'dark' : 'light';
    },
    perfilChartSeries() {
      return this.data.perfis.map(p => Number(p.totalUsuarios) || 0);
    },
    perfilChartOptions() {
      return {
        chart: { type: 'donut' },
        labels: this.data.perfis.map(p => p.perfil_acesso_nome || 'N/A'),
        colors: ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#0ea5e9'],
        legend: { position: 'bottom' },
        tooltip: { theme: this.chartTheme },
        responsive: [{ breakpoint: 480, options: { chart: { width: 280 } } }],
      };
    },
    pedidosMensaisSeries() {
      return [{ name: 'Pedidos', data: this.data.pedidosRecentes.map(p => Number(p.totalPedidos) || 0) }];
    },
    pedidosMensaisOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        xaxis: { categories: this.data.pedidosRecentes.map(p => p.mes || '') },
        colors: ['#2563eb'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
        dataLabels: { enabled: false },
        tooltip: { theme: this.chartTheme },
      };
    },
    faturamentoSeries() {
      return [{ name: 'Faturamento (R$)', data: this.data.pedidosRecentes.map(p => Number(p.valorTotal) || 0) }];
    },
    faturamentoOptions() {
      return {
        chart: { type: 'area', toolbar: { show: false } },
        xaxis: { categories: this.data.pedidosRecentes.map(p => p.mes || '') },
        colors: ['#16a34a'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 } },
        dataLabels: { enabled: false },
        yaxis: { labels: { formatter: val => 'R$ ' + Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) } },
        tooltip: { theme: this.chartTheme, y: { formatter: val => 'R$ ' + Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) } },
      };
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        const { data } = await axios.get('/dashboard/admin-dkp');
        this.data = data;
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar dados do dashboard',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* 
  B2B Premium Palette:
  1: #01094e (Deep Navy)
  2: #0575b5 (Ocean Blue)
  3: #00b7d6 (Vibrant Cyan)
  4: #8fdeea (Pale Sky Blue)
  5: #d6f4fa (Ice Blue)
*/

.dashboard-admin-dkp { 
  padding: 1rem; 
  --info-divider-color: rgba(143, 222, 234, 0.2); 
}

/* Header */
.page-header { margin-bottom: 2rem; }
.page-greeting {
  font-size: 1rem;
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0.15rem;
}
.page-title { 
  font-size: 1.75rem; 
  font-weight: 700; 
  color: #01094e; 
  margin-bottom: 0.25rem; 
  letter-spacing: -0.01em;
}
.page-subtitle { color: #0575b5; font-size: 1rem; font-weight: 400; opacity: 0.8; }

/* Tabs */
.dashboard-tabs-header {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.75rem;
  border-bottom: 2px solid rgba(143, 222, 234, 0.3);
  padding-bottom: 0px;
}
.dashboard-tab-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: #0575b5;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: all 0.3s ease;
  opacity: 0.7;
}
.dashboard-tab-btn:hover {
  color: #01094e;
  opacity: 1;
}
.dashboard-tab-btn.active {
  color: #01094e;
  border-bottom-color: #00b7d6;
  opacity: 1;
}

/* Stat Grid */
.stats-grid { 
  display: grid; 
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); 
  gap: 1.25rem; 
  margin-bottom: 1.5rem; 
}

/* Glassmorphic Stat Cards */
.stat-card { 
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(143, 222, 234, 0.4);
  border-radius: 12px; 
  padding: 1.5rem; 
  display: flex; 
  align-items: center; 
  gap: 1.25rem; 
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); 
  position: relative; 
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s ease;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
  border-color: rgba(0, 183, 214, 0.4);
}

/* Stat Icons */
.stat-icon { 
  width: 56px; 
  height: 56px; 
  border-radius: 12px; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  background: linear-gradient(135deg, rgba(5, 117, 181, 0.1), rgba(0, 183, 214, 0.1));
  color: #0575b5;
  border: 1px solid rgba(0, 183, 214, 0.15);
  transition: all 0.3s ease;
}
.stat-card:hover .stat-icon {
  background: linear-gradient(135deg, #0575b5, #00b7d6);
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 183, 214, 0.4);
  border-color: transparent;
}

/* Typography inside Stats */
.stat-label { 
  display: block; 
  font-size: 0.9rem; 
  font-weight: 500;
  color: #0575b5; 
  opacity: 0.8;
  margin-bottom: 0.25rem;
}
.stat-value { 
  display: block; 
  font-size: 1.75rem; 
  font-weight: 700; 
  color: #01094e; 
  line-height: 1.1;
}

/* Chart and Info Cards */
.chart-card, .info-card { 
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(143, 222, 234, 0.25);
  border-radius: 12px; 
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); 
  height: 100%; 
  transition: box-shadow 0.3s ease;
}
.chart-card:hover, .info-card:hover {
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
}
.card-chart-title { 
  font-size: 1.1rem; 
  font-weight: 700; 
  color: #01094e; 
  margin-bottom: 1.25rem; 
  letter-spacing: -0.01em;
}

/* Info Lists */
.info-list { display: flex; flex-direction: column; gap: 0.5rem; }
.info-item { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  padding: 0.75rem 0; 
  border-bottom: 1px solid var(--info-divider-color); 
}
.info-item:last-child { border-bottom: none; }
.info-label { color: #0575b5; font-size: 0.95rem; font-weight: 500; }
.info-value { font-weight: 700; font-size: 1.15rem; color: #01094e; }
.info-value.text-success { color: #00b7d6 !important; } /* Rebranded Success to Cyan */
.info-value.text-danger { color: #ff4b4b !important; }

.no-data { 
  text-align: center; 
  padding: 3rem; 
  color: #8fdeea; 
  font-size: 1rem; 
  font-weight: 500;
}

/* ================= Dark Layout Overrides ================= */
body.dark-layout .dashboard-admin-dkp {
  --info-divider-color: rgba(143, 222, 234, 0.1);
}
body.dark-layout .dashboard-admin-dkp .page-title {
  color: #d6f4fa;
}
body.dark-layout .dashboard-admin-dkp .page-subtitle {
  color: #8fdeea;
}
body.dark-layout .dashboard-admin-dkp .dashboard-tabs-header {
  border-bottom-color: rgba(143, 222, 234, 0.15);
}
body.dark-layout .dashboard-admin-dkp .dashboard-tab-btn {
  color: #8fdeea;
}
body.dark-layout .dashboard-admin-dkp .dashboard-tab-btn:hover {
  color: #d6f4fa;
}
body.dark-layout .dashboard-admin-dkp .dashboard-tab-btn.active {
  color: #00b7d6;
  border-bottom-color: #00b7d6;
}
body.dark-layout .dashboard-admin-dkp .stat-card,
body.dark-layout .dashboard-admin-dkp .chart-card,
body.dark-layout .dashboard-admin-dkp .info-card {
  background: rgba(1, 9, 78, 0.6);
  border-color: rgba(143, 222, 234, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
body.dark-layout .dashboard-admin-dkp .stat-card:hover,
body.dark-layout .dashboard-admin-dkp .chart-card:hover,
body.dark-layout .dashboard-admin-dkp .info-card:hover {
  background: rgba(1, 9, 78, 0.8);
  border-color: rgba(0, 183, 214, 0.3);
  box-shadow: 0 12px 40px rgba(0, 183, 214, 0.15);
}
body.dark-layout .dashboard-admin-dkp .stat-label,
body.dark-layout .dashboard-admin-dkp .info-label {
  color: #8fdeea;
}
body.dark-layout .dashboard-admin-dkp .stat-value,
body.dark-layout .dashboard-admin-dkp .info-value,
body.dark-layout .dashboard-admin-dkp .card-chart-title {
  color: #d6f4fa;
}
body.dark-layout .dashboard-admin-dkp .stat-icon {
  background: rgba(0, 183, 214, 0.1);
  color: #00b7d6;
  border-color: rgba(0, 183, 214, 0.2);
}
body.dark-layout .dashboard-admin-dkp .stat-card:hover .stat-icon {
  background: linear-gradient(135deg, #0575b5, #00b7d6);
  color: #fff;
  box-shadow: 0 0 15px rgba(0, 183, 214, 0.5);
  border-color: transparent;
}
body.dark-layout .dashboard-admin-dkp .no-data {
  color: #0575b5;
}
</style>
